import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors } from 'shared/styles/colors';
import { observer } from 'mobx-react-lite';
import testsApi from 'shared/api/tests-api';

const ModalWrapper = styled.div`
  @apply w-full max-w-3xl mx-auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const ScrollableContent = styled.div`
  @apply flex-1 overflow-y-auto;
  padding-right: 8px;
  min-height: 0;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
  }
`;

const TestImage = styled.img`
  width: 100%;
  max-width: 500px;
  height: auto;
  margin: 0 auto 30px auto;
  display: block;
  border-radius: 8px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: ${colors.accentSecondary};
  margin-bottom: 24px;
  text-align: center;
`;

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 16px;
  text-transform: uppercase;
`;

const ContentText = styled.div`
  font-size: 16px;
  line-height: 1.6;
  color: #333;
  text-align: justify;
  margin-bottom: 20px;
  
  p {
    margin-bottom: 16px;
  }
  
  strong {
    font-weight: 600;
    color: ${colors.accentPrimary};
  }
  
  /* Handle HTML content */
  ul, ol {
    margin: 16px 0;
    padding-left: 20px;
  }
  
  li {
    margin-bottom: 8px;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
`;

const LoadingText = styled.div`
  font-size: 16px;
  color: #666;
`;

const ErrorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  color: #dc3545;
  text-align: center;
`;

interface TestDetailsModalProps {
  testData: {
    id: number;
    name: string;
    type: string;
    structure: string;
    picture: string;
    description: string;
    price: number;
    price_currency: string;
    number_of_questions: number;
    available_time: number;
  };
}

interface ApiResponse {
  test_info: {
    name: string;
    content: string;
  };
  notification_count: number;
  expired_purchases: any[];
}

// Map test types to slugs
const getTestSlug = (type: string): string => {
  switch (type) {
    case 'verbal':
      return 'verbalnij';
    case 'math':
      return 'abstraktnij';
    case 'logic':
      return 'logichnij';
    default:
      return type;
  }
};

// Helper function to decode HTML entities and fix image URLs
const processHtmlContent = (htmlString: string): string => {
  const textArea = document.createElement('textarea');
  textArea.innerHTML = htmlString;
  let processedContent = textArea.value;
  
  // Fix relative image URLs by prepending the backend domain
  const backendUrl = process.env.REACT_APP_API_URL || 'https://admin.vkksu.com';
  processedContent = processedContent.replace(
    /src="\/media\//g,
    `src="${backendUrl}/media/`
  );
  
  return processedContent;
};

export const TestDetailsModal: React.FC<TestDetailsModalProps> = observer(({ testData }) => {
  const [apiResponse, setApiResponse] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTestInfo = async () => {
      if (!testData) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const slug = getTestSlug(testData.type);
        console.log('🔍 Fetching test info for slug:', slug);
        
        const response = await testsApi.getTestInfo(slug);
        console.log('✅ Test info response:', response.data);
        
        setApiResponse(response.data);
      } catch (err: any) {
        console.error('❌ Error fetching test info:', err);
        setError('Не вдалося завантажити детальну інформацію про тест');
      } finally {
        setLoading(false);
      }
    };

    fetchTestInfo();
  }, [testData]);

  if (!testData) return null;

  if (loading) {
    return (
      <ModalWrapper>
        <LoadingWrapper>
          <LoadingText>Завантаження...</LoadingText>
        </LoadingWrapper>
      </ModalWrapper>
    );
  }

  if (error) {
    return (
      <ModalWrapper>
        <ErrorWrapper>
          <div>{error}</div>
        </ErrorWrapper>
      </ModalWrapper>
    );
  }

  if (!apiResponse || !apiResponse.test_info) {
    return (
      <ModalWrapper>
        <ErrorWrapper>
          <div>Інформація про тест не знайдена</div>
        </ErrorWrapper>
      </ModalWrapper>
    );
  }

  const testInfo = apiResponse.test_info;

  // Check if content contains HTML tags
  const hasHtmlTags = /<[^>]+>/.test(testInfo.content) || /&lt;[^&]*&gt;/.test(testInfo.content);
  const isHtmlContent = hasHtmlTags;

  return (
    <ModalWrapper>
      <ScrollableContent>
        <ContentText>
          {isHtmlContent ? (
            <div dangerouslySetInnerHTML={{ __html: processHtmlContent(testInfo.content) }} />
          ) : (
            <div>{testInfo.content}</div>
          )}
        </ContentText>
      </ScrollableContent>
    </ModalWrapper>
  );
});